import { difference } from 'lodash';

import { BriefingListItemApiModel } from '../../../api/briefings/model/briefing-list-item-api.model';
import { BriefingStatusEnum, briefingStatusMapFromApi, briefingStatusMapToApi } from '../enum/briefing-status.enum';
import { AuditableModel } from '../../Misc/models/auditable.model';
import { TimeHelper } from '../../Forms/helpers/time.helper';
import { BriefingTypeEnum } from '../../../Monitor/Briefings/enums/briefing-type.enum';
import { LeftSidebarListItemConvertableModel } from '../../../Common/Layout/models/left-sidebar-list-item-convertable.model';
import { LeftSidebarListItemButton } from '../../../Common/Layout/models/left-sidebar-list-item-button.model';
import { LeftSidebarListItem } from '../../../Common/Layout/models/left-sidebar-list-item.model';
import { ActionsEnum } from '../../Basic/enums/actions.enum';
import { AlertTypeEnum } from '../enum/alert-type.enum';
import { BriefingApiModel } from '../../../api/briefings/model/briefing-api.model';
import { BriefingDeliveryMethodEnum } from '../../../Monitor/Briefings/enums/briefing-delivery-method.enum';
import { BaseModel } from '../../../Common/Basic/models/base.model';

export class BriefingListItemModel extends BaseModel<BriefingListItemModel, BriefingListItemApiModel>
    implements AuditableModel, LeftSidebarListItemConvertableModel {

    id: number;
    name: string;

    status: BriefingStatusEnum;
    type: BriefingTypeEnum;
    searchCriteriaId: number;

    feedIds: number[];
    alertType: AlertTypeEnum;
    deliveryMethod: BriefingDeliveryMethodEnum;

    hidden?: boolean;
    groupId: number;
    isFavorite: boolean;

    // readonly
    createdByUserId?: number;
    createdDate?: Date;

    modifiedByUserId?: number;
    modifiedDate?: Date;


    constructor(apiModel?: BriefingListItemApiModel) {
        super();

        if (apiModel) {
            this.constructFromApiModel(apiModel);
        } else {
            this.constructDefault();
        }
    }

    static createFromBriefingApiModel(briefingApiModel: BriefingApiModel): BriefingListItemModel {
        return new BriefingListItemModel({
            id: briefingApiModel.briefingId,
            name: briefingApiModel.briefingName,
            status: briefingApiModel.status,
            searchCriteriaId: briefingApiModel.searchCriteriaId,
            createdByUserId: briefingApiModel.createdByUserId,
            createdDate: briefingApiModel.createdDate,
            modifiedByUserId: briefingApiModel.modifiedByUserId,
            modifiedDate: briefingApiModel.modifiedDate,
            feedIds: briefingApiModel.feeds?.map(x => x.id) || [],
            hidden: briefingApiModel.hidden,
            displayGroupId: briefingApiModel.displayGroupId,
            alertType: briefingApiModel.alertType,
            deliveryMethod: briefingApiModel.deliveryMethod ? briefingApiModel.deliveryMethod : BriefingDeliveryMethodEnum.EMAIL,
        });
    }

    constructFromApiModel(apiModel: BriefingListItemApiModel) {
        this.id = apiModel.id;
        this.name = apiModel.name;
        this.status = briefingStatusMapFromApi.get(apiModel.status);
        this.searchCriteriaId = apiModel.searchCriteriaId;
        this.createdByUserId = apiModel.createdByUserId;
        this.createdDate = new Date(TimeHelper.convertTo13DigitTimestamp(apiModel.createdDate));
        this.modifiedByUserId = apiModel.modifiedByUserId;
        this.modifiedDate = apiModel.modifiedDate ? new Date(TimeHelper.convertTo13DigitTimestamp(apiModel.modifiedDate)) : null;
        this.feedIds = apiModel.feedIds;
        this.type = apiModel.briefingType;
        this.hidden = apiModel.hidden;
        this.groupId = apiModel.displayGroupId;
        this.isFavorite = apiModel.isFavorited;
        this.deliveryMethod = apiModel.deliveryMethod ? apiModel.deliveryMethod : BriefingDeliveryMethodEnum.EMAIL;
        if (apiModel.alertType) {
            this.alertType = apiModel.alertType;
        } else {
            this.alertType = AlertTypeEnum.RECENT;
        }
    }

    constructDefault() {
        this.id = null;
        this.name = null;

        this.status = null;
        this.searchCriteriaId = null;
        this.feedIds = [];
        this.type = null;
        this.alertType = AlertTypeEnum.RECENT;
        this.deliveryMethod = BriefingDeliveryMethodEnum.EMAIL;
        this.hidden = null;
        this.isFavorite = false;

        this.createdByUserId = null;
        this.createdDate = null;
        this.modifiedByUserId = null;
        this.modifiedDate = null;
    }

    toApiModel(): BriefingListItemApiModel {
        const apiModel: BriefingListItemApiModel = {
            id: this.id,
            name: this.name,
            status: briefingStatusMapToApi.get(this.status),
            searchCriteriaId: this.searchCriteriaId,
            feedIds: this.feedIds,
            briefingType: this.type,
            alertType: this.alertType,
            deliveryMethod: this.deliveryMethod,
            hidden: this.hidden,
            displayGroupId: this.groupId,
            isFavorited: this.isFavorite,
            createdByUserId: this.createdByUserId,
            createdDate: this.createdDate.getTime(),
            modifiedByUserId: this.modifiedByUserId,
            modifiedDate: this.modifiedDate.getTime()
        };

        return apiModel;
    }

    clone(): BriefingListItemModel {
        return new BriefingListItemModel(this.toApiModel());
    }


    equals(other?: BriefingListItemModel): boolean {
        if (!other) {
            return false;
        }

        if (this.id !== other.id) {
            return false;
        }

        if (this.name !== other.name) {
            return false;
        }

        if (this.status !== other.status) {
            return false;
        }

        if (this.type !== other.type) {
            return false;
        }

        if (this.searchCriteriaId !== other.searchCriteriaId) {
            return false;
        }

        if (difference(this.feedIds, other.feedIds)?.length > 0) {
            return false;
        }

        if (this.alertType !== other.alertType) {
            return false;
        }

        if (this.deliveryMethod !== other.deliveryMethod) {
            return false;
        }

        if (this.hidden !== other.hidden) {
            return false;
        }

        if (this.groupId !== other.groupId) {
            return false;
        }

        if (this.isFavorite !== other.isFavorite) {
            return false;
        }

        return true;
    }


    toLeftSidebarListItem(buttons?: LeftSidebarListItemButton[]): LeftSidebarListItem {
        const item = new LeftSidebarListItem();
        item.value = this.id;
        item.label = this.name;
        item.status = `Briefings.status.${this.status}`;

        if (this.status === BriefingStatusEnum.SENT || this.status === BriefingStatusEnum.SCHEDULED) {
            buttons = buttons.filter(button => button.key !== ActionsEnum.rename);
        }

        if (this.type === BriefingTypeEnum.ALERT) {
            switch (this.alertType) {
                case AlertTypeEnum.SURGE:
                    item.icon = 'fa-kit fa-agpr-momentum';
                    item.iconAriaLabel = 'Monitor.leftSidebar.alerts.surge.ariaLabel';
                    break;
                case AlertTypeEnum.RECENT:
                default:
                    item.icon = 'fa-solid fa-bell-exclamation';
                    item.iconAriaLabel = 'Monitor.leftSidebar.alerts.recent.ariaLabel';
                    break;
            }
        }

        if (this.type === BriefingTypeEnum.BRIEFING) {
            switch (this.deliveryMethod) {
                case BriefingDeliveryMethodEnum.ONLINE:
                    item.icon = 'fa-duotone fa-regular fa-frame';
                    item.iconAriaLabel = 'Monitor.leftSidebar.briefings.online.ariaLabel';
                    break;
                case BriefingDeliveryMethodEnum.EMAIL:
                default:
                    item.icon = 'fa-solid fa-envelope';
                    item.iconAriaLabel = 'Monitor.leftSidebar.briefings.email.ariaLabel';
                    break;
            }
        }
        if (this.hidden) {
            buttons = buttons.filter(button => button.key !== ActionsEnum.hide);
        } else {
            buttons = buttons.filter(button => button.key !== ActionsEnum.show);
        }

        if (this.isFavorite) {
            buttons = buttons.filter(button => button.key !== ActionsEnum.favorite);
        } else {
            buttons = buttons.filter(button => button.key !== ActionsEnum.unfavorite);
        }

        item.buttons = buttons;
        item.groupId = this.groupId;
        item.isFavorite = this.isFavorite;
        (item as any).deliveryMethod = this.deliveryMethod;

        return item;
    }
}
